import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import DirectionsCarTwoToneIcon from '@mui/icons-material/DirectionsCarTwoTone';
import EventSeatTwoToneIcon from '@mui/icons-material/EventSeatTwoTone';
import PersonTwoToneIcon from '@mui/icons-material/PersonTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import RequestQuoteTwoToneIcon from '@mui/icons-material/RequestQuoteTwoTone';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import PropTypes from 'prop-types';

import { searchAutoClever } from 'src/api/globalSearch';

const resultIcons = {
  work_order: ReceiptLongTwoToneIcon,
  customer: PersonTwoToneIcon,
  vehicle: DirectionsCarTwoToneIcon,
  inventory: StorefrontTwoToneIcon,
  test_drive: EventSeatTwoToneIcon,
  finance_lead: RequestQuoteTwoToneIcon
};

const requestErrorMessage = (error) => error?.response?.data?.error
  || error?.message
  || 'Search is temporarily unavailable.';

function SearchResults({ query, groups, loading, error, onSelect }) {
  const hasQuery = String(query || '').trim().length >= 2;
  const hasResults = groups.some((group) => group.items?.length);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box px={2} py={3}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!hasQuery) {
    return (
      <Box px={2} py={3}>
        <Typography color="text.secondary" variant="body2">
          Search by name, phone, RO number, VIN, plate, or inventory vehicle.
        </Typography>
      </Box>
    );
  }

  if (!hasResults) {
    return (
      <Box px={2} py={3}>
        <Typography color="text.secondary" variant="body2">
          No matching records found.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding sx={{ maxHeight: 500, overflowY: 'auto', overscrollBehavior: 'contain' }}>
      {groups.map((group, groupIndex) => (
        <Box key={group.type}>
          {groupIndex ? <Divider /> : null}
          <Typography
            component="div"
            sx={{
              px: 1.5,
              pt: 1.1,
              pb: 0.45,
              color: 'text.secondary',
              fontSize: '0.6875rem',
              fontWeight: 800,
              textTransform: 'uppercase'
            }}
          >
            {group.label}
          </Typography>
          {group.items.map((item) => {
            const ResultIcon = resultIcons[group.type] || SearchTwoToneIcon;
            return (
              <ListItemButton
                key={`${group.type}-${item.id}`}
                onClick={() => onSelect(item)}
                sx={{ px: 1.5, py: 0.8, alignItems: 'center' }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: 'primary.main' }}>
                  <ResultIcon sx={{ fontSize: 19 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.subtitle || null}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.8125rem', fontWeight: 700 }}
                  secondaryTypographyProps={{ noWrap: true, fontSize: '0.72rem' }}
                  sx={{ minWidth: 0, my: 0 }}
                />
                {item.meta ? (
                  <Typography
                    noWrap
                    color="text.secondary"
                    sx={{ ml: 1, maxWidth: 140, fontSize: '0.7rem', fontWeight: 650 }}
                  >
                    {item.meta}
                  </Typography>
                ) : null}
              </ListItemButton>
            );
          })}
        </Box>
      ))}
    </List>
  );
}

SearchResults.propTypes = {
  query: PropTypes.string.isRequired,
  groups: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

function GlobalSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      requestSequenceRef.current += 1;
      setGroups([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await searchAutoClever(trimmed);
        if (requestSequenceRef.current !== requestId) return;
        setGroups(response.groups || []);
      } catch (requestError) {
        if (requestSequenceRef.current !== requestId) return;
        setGroups([]);
        setError(requestErrorMessage(requestError));
      } finally {
        if (requestSequenceRef.current === requestId) setLoading(false);
      }
    }, 280);

    return () => window.clearTimeout(timer);
  }, [query]);

  const openSearch = useCallback(() => {
    if (mobile) {
      setMobileOpen(true);
      window.setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [mobile]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }
      if (event.key === 'Escape') {
        setOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [openSearch]);

  const selectResult = (item) => {
    setOpen(false);
    setMobileOpen(false);
    setQuery('');
    navigate(item.path);
  };

  const searchField = (autoFocus = false) => (
    <TextField
      fullWidth
      autoFocus={autoFocus}
      inputRef={inputRef}
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      onFocus={() => {
        if (!mobile) setOpen(true);
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          document.querySelector('[data-global-search-results] .MuiListItemButton-root')?.focus();
        }
      }}
      size="small"
      placeholder="Search AutoClever"
      InputProps={{
        inputProps: { 'aria-label': 'Search AutoClever records', autoComplete: 'off' },
        startAdornment: (
          <InputAdornment position="start">
            <SearchTwoToneIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
          </InputAdornment>
        ),
        endAdornment: !mobile ? (
          <InputAdornment position="end">
            <Box
              component="kbd"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                px: 0.6,
                py: 0.15,
                color: 'text.secondary',
                bgcolor: 'background.default',
                fontFamily: 'inherit',
                fontSize: '0.65rem'
              }}
            >
              Ctrl K
            </Box>
          </InputAdornment>
        ) : null
      }}
      sx={{
        '& .MuiOutlinedInput-root': { height: 38, bgcolor: 'background.default' },
        '& .MuiInputBase-input': { fontSize: '0.8125rem' }
      }}
    />
  );

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box
          ref={anchorRef}
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: 'min(38vw, 460px)',
            minWidth: { sm: 220, lg: 320 },
            ml: 0.5
          }}
        >
          {searchField()}
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            disablePortal={false}
            container={() => document.body}
            style={{ zIndex: 20000 }}
            sx={{ zIndex: '20000 !important' }}
          >
            <Paper
              data-global-search-results
              elevation={8}
              sx={{
                mt: 0.75,
                width: anchorRef.current?.clientWidth || 420,
                overflow: 'hidden',
                position: 'relative',
                zIndex: 1
              }}
            >
              <SearchResults
                query={query}
                groups={groups}
                loading={loading}
                error={error}
                onSelect={selectResult}
              />
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      <Tooltip title="Search">
        <IconButton
          color="primary"
          aria-label="Search AutoClever"
          onClick={openSearch}
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <SearchTwoToneIcon />
        </IconButton>
      </Tooltip>

      <Dialog fullWidth maxWidth="sm" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <DialogContent sx={{ p: 1.25 }}>
          {searchField(true)}
          <Box data-global-search-results sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 0.75, overflow: 'hidden' }}>
            <SearchResults
              query={query}
              groups={groups}
              loading={loading}
              error={error}
              onSelect={selectResult}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GlobalSearch;
